import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  selectPulmonologyError,
  selectPulmonologyLoading,
  selectPulmonologyPatientRecords,
} from "../../redux/selectors/pulmonologySelectors";
import { fetchPulmonologyRecords } from "../../redux/thunks/pulmonologyThunks";
import "./PulmonologyHistory.css";

const resourceTitleKeys = {
  "reason-symptoms": "page.sections.reason",
  "physical-examination": "page.sections.vitals",
  "suspected-diagnoses": "page.sections.diagnoses",
  imaging: "page.sections.imaging",
  "pulmonary-function-tests": "page.sections.pft",
  bronchoscopy: "page.sections.bronchoscopy",
  "specific-biology": "page.sections.biology",
  "allergy-workup": "page.sections.allergy",
  "conclusion-plan": "page.sections.plan",
};

const excludedFields = new Set([
  "id",
  "_id",
  "patientId",
  "doctorId",
  "createdAt",
  "updatedAt",
]);

const formatLabel = (value) => String(value)
  .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
  .replace(/[-_]/g, " ")
  .replace(/^\w/, (letter) => letter.toUpperCase());

const formatValue = (value) => {
  if (value == null || value === "") return "—";
  if (Array.isArray(value)) return value.map(formatValue).join(", ");
  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, item]) => `${formatLabel(key)}: ${formatValue(item)}`)
      .join(" · ");
  }
  if (typeof value === "boolean") return value ? "✓" : "—";
  return String(value);
};

function HistoryTable({ columns, rows }) {
  if (!rows.length) return null;
  return (
    <div className="pulmo-history-table-wrap">
      <table className="pulmo-history-table">
        <thead>
          <tr>{columns.map((column) => <th key={column.key}>{column.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id || row.parameter || row.name || index}>
              {columns.map((column) => <td key={column.key}>{formatValue(row[column.key])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecordDetails({ resource, record, t }) {
  if (resource === "pulmonary-function-tests") {
    return (
      <>
        <span className="pulmo-history-type">{record.testType}</span>
        <HistoryTable
          columns={[
            { key: "parameter", label: t("pft.parameter") },
            { key: "measured", label: t("pft.measured") },
            { key: "predicted", label: t("pft.predicted") },
            { key: "percentPredicted", label: t("pft.percentPredicted") },
            { key: "lln", label: t("pft.lln") },
            { key: "zScore", label: t("pft.zScore") },
            { key: "postBD", label: t("pft.postBd") },
            { key: "change", label: t("pft.change") },
          ]}
          rows={record.results || []}
        />
      </>
    );
  }

  if (resource === "allergy-workup") {
    return (
      <>
        <HistoryTable
          columns={[
            { key: "name", label: t("allergy.result") },
            { key: "size", label: t("allergy.whealSize") },
            { key: "status", label: t("allergy.result") },
          ]}
          rows={record.prickTests || []}
        />
        <HistoryTable
          columns={[
            { key: "name", label: t("allergy.ige.total") },
            { key: "value", label: t("allergy.igeDetails.tested") },
            { key: "detail", label: t("allergy.editDetail") },
          ]}
          rows={record.igeResults || []}
        />
      </>
    );
  }

  if (resource === "physical-examination") {
    return (
      <>
        {record.vitalSigns && <HistoryTable
          columns={Object.keys(record.vitalSigns).map((key) => ({
            key,
            label: t(`vitals.fields.${key}`, { defaultValue: formatLabel(key) }),
          }))}
          rows={[record.vitalSigns]}
        />}
        <HistoryTable
          columns={[
            { key: "name", label: t("vitals.examTitle") },
            { key: "isNormal", label: t("common.normal") },
            { key: "findings", label: t("vitals.auscultation") },
            { key: "rightZones", label: t("vitals.sides.right") },
            { key: "leftZones", label: t("vitals.sides.left") },
            { key: "notes", label: t("vitals.notesPlaceholder") },
          ]}
          rows={record.examinations || []}
        />
      </>
    );
  }

  if (resource === "reason-symptoms") {
    return (
      <>
        <HistoryTable
          columns={[
            { key: "name", label: t("reason.fields.symptom") },
            { key: "duration", label: t("reason.fields.duration") },
            { key: "severity", label: t("reason.fields.severity") },
            { key: "triggerContext", label: t("reason.fields.trigger") },
          ]}
          rows={record.symptoms || []}
        />
        <HistoryTable
          columns={[
            { key: "reasons", label: t("reason.title") },
            { key: "mmrcDyspneaScale", label: t("reason.scale") },
          ]}
          rows={[record]}
        />
        {record.symptomNarrative && <p className="pulmo-history-text">{record.symptomNarrative}</p>}
      </>
    );
  }

  if (resource === "suspected-diagnoses") {
    return <HistoryTable
      columns={[{ key: "diagnoses", label: t("diagnoses.presetsTitle") }]}
      rows={(record.diagnoses || []).map((diagnosis) => ({ diagnoses: diagnosis }))}
    />;
  }

  if (resource === "conclusion-plan") {
    return <p className="pulmo-history-text">{record.conclusionPlan || "—"}</p>;
  }

  const rows = Object.entries(record)
    .filter(([key, value]) => !excludedFields.has(key) && value != null && value !== "")
    .map(([key, value]) => ({ field: formatLabel(key), value }));

  return <HistoryTable
    columns={[
      { key: "field", label: t("page.historyField") },
      { key: "value", label: t("page.historyValue") },
    ]}
    rows={rows}
  />;
}

const getRecordDate = (record) =>
  record?.updatedAt || record?.createdAt || record?.procedureDate || record?.dateDescription;

export default function PulmonologyHistory({ patientId }) {
  const { t, i18n } = useTranslation("pulmonology");
  const dispatch = useDispatch();
  const records = useSelector((state) => selectPulmonologyPatientRecords(state, patientId));
  const loading = useSelector(selectPulmonologyLoading);
  const error = useSelector(selectPulmonologyError);

  useEffect(() => {
    if (patientId) dispatch(fetchPulmonologyRecords({ patientId }));
  }, [dispatch, patientId]);

  const historyItems = Object.entries(records)
    .flatMap(([resource, items]) => (items || []).map((record) => ({ resource, record })))
    .sort((left, right) => new Date(getRecordDate(right.record) || 0) - new Date(getRecordDate(left.record) || 0));

  return (
    <section className="pulmo-history" aria-labelledby="pulmo-history-title">
      <h2 id="pulmo-history-title">{t("page.historyTitle")}</h2>
      {loading && <p role="status">{t("page.loadingHistory")}</p>}
      {error && <p className="pulmo-history-error" role="alert">{error}</p>}
      {!loading && historyItems.length === 0 && <p className="pulmo-history-empty">{t("page.noHistory")}</p>}
      <div className="pulmo-history-list">
        {historyItems.map(({ resource, record }) => {
          const date = getRecordDate(record);
          const dateLabel = date && !Number.isNaN(new Date(date).getTime())
            ? new Date(date).toLocaleString(i18n.language || "en", { dateStyle: "medium", timeStyle: "short" })
            : t("page.recorded");
          return (
            <article className="pulmo-history-card" key={`${resource}-${record.id || record._id}`}>
              <header>
                <h3>{t(resourceTitleKeys[resource] || "page.pulmonology")}</h3>
                <time>{dateLabel}</time>
              </header>
              <RecordDetails resource={resource} record={record} t={t} />
            </article>
          );
        })}
      </div>
    </section>
  );
}